import { LinkAction } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText, TextInput } from './content-fields'
import type { Translate, UsageLinkView } from './content-types'

/**
 * Props for {@link TextHeaderView}.
 *
 * @property usageLinks - "Used by" links (tenant defaults, then each product), opened through `onNavigate`.
 * @property onSave - Present only with `content.write`; otherwise name/description are read-only.
 */
export interface TextHeaderViewProps {
	t: Translate
	code: string
	typeName: string
	sourceLocale: string
	name: string
	description: string
	missingLocales: string[]
	usageLinks: UsageLinkView[]
	errors: Record<string, string>
	formError: string | null
	isBusy: boolean
	onChange: (field: 'name' | 'description', value: string) => void
	onNavigate: (href: string) => void
	onSave?: () => void
}

function HeaderFacts({ props }: { props: TextHeaderViewProps }): ReactElement {
	const { t } = props
	return (
		<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-ds-sm">
			<dt className="text-ds-text-muted">{t('header.type')}</dt>
			<dd>{props.typeName}</dd>
			<dt className="text-ds-text-muted">{t('header.sourceLocale')}</dt>
			<dd>{props.sourceLocale}</dd>
			<dt className="text-ds-text-muted">{t('header.usedBy')}</dt>
			<dd className="flex flex-wrap gap-2">
				{props.usageLinks.length
					? props.usageLinks.map((link) => (
							<LinkAction key={link.href} onPress={() => props.onNavigate(link.href)}>
								{link.label}
							</LinkAction>
						))
					: t('usage.none')}
			</dd>
		</dl>
	)
}

/** The text header: immutable code, editable name/description, type, source language, missing languages, usage (organism). */
export function TextHeaderView(props: TextHeaderViewProps): ReactElement {
	const { t } = props
	const readOnly = !props.onSave
	return (
		<header className="flex flex-col gap-3">
			<h1 className="text-ds-xl font-semibold text-ds-text">{props.code}</h1>
			<HeaderFacts props={props} />
			{props.missingLocales.length ? (
				<p role="status" className="text-ds-sm text-ds-danger">
					{t('header.missing', { locales: props.missingLocales.join(', ') })}
				</p>
			) : null}
			<ErrorText message={props.formError} />
			<TextInput
				label={t('form.name')}
				value={props.name}
				error={props.errors.name}
				disabled={readOnly}
				onChange={(v) => props.onChange('name', v)}
			/>
			<TextInput
				label={t('form.description')}
				value={props.description}
				error={props.errors.description}
				disabled={readOnly}
				onChange={(v) => props.onChange('description', v)}
			/>
			{props.onSave ? (
				<div>
					<Button isDisabled={props.isBusy} onPress={props.onSave}>
						{t('header.save')}
					</Button>
				</div>
			) : null}
		</header>
	)
}
